// src/redux/axiosInstance.js
import axios from "axios";
import env from "./config/env";

import { Modal } from "antd";
import { store } from "./store";
import { showToast } from "../utils/toastService";
import { loadState, clearState } from "./config/sessionPersist";
import "../common/forceLogout.css";

let isSessionModalVisible = false;

/**
 * Normalise axios error into a safe AppError object
 * – no stack traces or internal details exposed to user
 */
const buildAppError = (error) => {
  if (error?.response) {
    const { status, data } = error.response;
    const backendMessage =
      typeof data?.message === "string" && data.message.trim().length
        ? data.message.trim()
        : typeof data?.error === "string" && data.error.trim().length
          ? data.error.trim()
          : data?.error?.message || null;

    return {
      status,
      code: data?.code || data?.errorCode || null,
      message:
        backendMessage ||
        (status >= 500
          ? "Server error. Please try again later."
          : "Request failed. Please check your input and try again."),
    };
  }

  if (error?.request) {
    return {
      status: undefined,
      code: "NETWORK_ERROR",
      message: "Network error. Please check your internet connection.",
    };
  }

  return {
    status: undefined,
    code: "UNKNOWN_ERROR",
    message: "Something went wrong. Please try again.",
  };
};

/**
 * Decide whether we should show toast for this error
 * (We skip 401 that trigger logout modal, and some auth APIs)
 */
const shouldShowToast = (status, url = "") => {
  // 401 is handled via session modal / refresh logic
  if (status === 401) return false;

  const toastSkipApis = [
    "user-service/isFirstTimeLoginWithLoginId",
    "/user-service/login",
    "/user-service/refresh",
    "/user-service/customLogout",
  ];

  if (toastSkipApis.some((api) => url.includes(api))) return false;

  return true;
};

// ------------------------ axios instance ------------------------

const instance = axios.create({
  baseURL: env.API_BASE_URL,
});

const getStoredToken = (authState) =>
  authState?.accessToken || authState?.token || null;


instance.interceptors.request.use(
  (config) => {
    const persistedToken = getStoredToken(loadState()?.auth);
    const localToken = localStorage.getItem("accessToken");
    const state = store.getState();
    const reduxToken = getStoredToken(state?.auth);

    // If caller provided Authorization explicitly, respect that
    const headerToken = config?.headers?.Authorization;

    if (!headerToken) {
      const token = persistedToken || localToken || reduxToken;
      if (token) {
        // Do not log token for security reasons
        config.headers.Authorization = `Bearer ${token}`;
      }
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// ---------------------------------------------
// ✅ RESPONSE INTERCEPTOR
// Handles 401 refresh + global error handling with Toast
// ---------------------------------------------
instance.interceptors.response.use(
  (response) => response,

  (error) => {
    const originalRequest = error.config || {};
    const status = error?.response?.status;
    const url = originalRequest?.url || "";

    const skipApis = [
      "/user-service/customLogout",
      "/user-service/login",
      "/user-service/refresh",
    ];

    const shouldSkip = skipApis.some((api) => url.includes(api));

    // ------- 401 → force logout -------
    if (status === 401 && !shouldSkip) {
      showSessionExpiredModal();
      return Promise.reject(error);
    }

    // ------- Build normalised error and attach it -------
    const appError = buildAppError(error);
    error.appError = appError;

    // ------- Show toast for non-401 errors -------
    if (shouldShowToast(appError.status, url)) {
      showToast({
        severity: "error",
        summary: "Error",
        detail: appError.message,
        life: 5000,
      });
    }

    return Promise.reject(error);
  }
);

// ---------------------------------------------
// Session expired — shows AntD warning modal directly
// ---------------------------------------------
function showSessionExpiredModal() {
  if (isSessionModalVisible) return;
  isSessionModalVisible = true;

  setTimeout(() => {
    Modal.warning({
      title: "Logged Out",
      content:
        "You have been logged out from the system. Click OK to go back to the Login Screen.",
      okText: "Ok",
      centered: true,
      maskClosable: false,
      className: "force-logout-modal",
      icon: null,
      onOk: () => {
        try {
          store.dispatch({ type: "SESSION_EXPIRED" });
          clearState();
          localStorage.clear();
        } catch (e) {
          if (process.env.NODE_ENV === "development") {
            console.error("Error clearing session:", e);
          }
        }
        isSessionModalVisible = false;
        window.location.assign(process.env.REACT_APP_HOME_URL);
      },
      afterClose: () => {
        isSessionModalVisible = false;
      },
    });
  }, 100);
}

export default instance;
